import FileRow from "components/file-components/file-row";
import ReadMoreText from "components/ReadMoreText";
import React from "react";
import { AssetAppeal, Report } from "types";
import AssetDetailCard from "./AssetDetailsCard";

function DailyReportCard({
  report,
  fileRowProps,
  isAssetRecoveryReport,
  setIsAppealPopupOpen,
  appeal,
  isAppeal,
  appealId,
  prohibitionId,
}: {
  report: Report;
  fileRowProps: any;
  isAssetRecoveryReport?: boolean;
  setIsAppealPopupOpen?: React.Dispatch<
    React.SetStateAction<{ open: boolean; data: any }>
  >;
  appeal?: AssetAppeal;
  isAppeal?: boolean;
  appealId?: string;
  prohibitionId: string | null;
}) {
  const isFinalVerdict = report.is_next_appointment_reason_final_verdict;

  const assetsArray = isAssetRecoveryReport
    ? report?.asset
    : isAppeal
      ? report?.asset
      : report?.asset;

  const getAssetsAsArray = () => {
    if (!assetsArray) return [];

    if (Array.isArray(assetsArray)) {
      return assetsArray;
    }

    if (assetsArray && typeof assetsArray === "object") {
      return [assetsArray];
    }

    return [];
  };

  const assetsToDisplay = getAssetsAsArray();
  const hasAssets = assetsToDisplay.length > 0;

  return (
    <div
      className={`border rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow ${
        isFinalVerdict ? "bg-black " : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center space-x-2 mb-3 justify-between">
        <div className="flex">
          <div
            className={`p-2 rounded-full mr-2 ${
              isFinalVerdict ? "bg-gray-700" : "bg-gray-100"
            }`}
          >
            {isFinalVerdict ? (
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M30 33.38V26H33V31.64L37.88 34.46L36.38 37.06L30 33.38ZM6 44V6H42V22.2C44.48 24.72 46 28.18 46 32C46 39.74 39.74 46 32 46C30.0727 46.0054 28.1653 45.6099 26.399 44.8386C24.6327 44.0674 23.0461 42.9372 21.74 41.52L18 44L12 40L6 44ZM19.34 26C20.06 24.5 21 23.14 22.2 22H14V26H19.34ZM34 18V14H14V18H34ZM32 42C37.52 42 42 37.52 42 32C42 26.48 37.52 22 32 22C26.48 22 22 26.48 22 32C22 37.52 26.48 42 32 42Z"
                  fill="white"
                />
              </svg>
            ) : (
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M24 27.38V20H27V25.64L31.88 28.46L30.38 31.06L24 27.38ZM0 38V0H36V16.2C38.48 18.72 40 22.18 40 26C40 33.74 33.74 40 26 40C24.0727 40.0054 22.1653 39.6099 20.399 38.8386C18.6327 38.0674 17.0461 36.9372 15.74 35.52L12 38L6 34L0 38ZM13.34 20C14.06 18.5 15 17.14 16.2 16H8V20H13.34ZM28 12V8H8V12H28ZM26 36C31.52 36 36 31.52 36 26C36 20.48 31.52 16 26 16C20.48 16 16 20.48 16 26C16 31.52 20.48 36 26 36Z"
                  fill={"#212121"}
                />
              </svg>
            )}
          </div>
          <div className="flex flex-col">
            <p
              className={`text-sm font-medium mb-1 ${
                isFinalVerdict ? "text-white" : "text-gray-500"
              }`}
            >
              {new Date(
                isFinalVerdict
                  ? (report.court_decision_date as string)
                  : report?.court_hearing_date,
              ).toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            <div>
              <p
                className={`text-sm font-medium ${
                  isFinalVerdict ? "text-white" : "text-gray-500"
                }`}
              >
                {isFinalVerdict
                  ? "Final Verdict"
                  : `for ${new Date(
                      isFinalVerdict
                        ? (report.court_decision_date as string)
                        : report?.next_appointment_date,
                    ).toLocaleDateString(undefined, {
                      weekday: "short",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {isFinalVerdict ? (
          // Final Verdict Content
          <>
            <div>
              <p className="font-semibold text-white">Court Decision Summary</p>
              <p className="text-white text-sm">
                <ReadMoreText
                  maxLines={3}
                  text={report.court_decision_summary as string}
                  color="white"
                />
              </p>
            </div>
            {report.prosecutor_feedback_on_prohabition_decision && (
              <div>
                <p className="font-semibold text-white">Prosecutor Feedback</p>
                <p className="text-white text-sm">
                  <ReadMoreText
                    maxLines={3}
                    text={report.prosecutor_feedback_on_prohabition_decision}
                    color="white"
                  />
                </p>
              </div>
            )}
            {report.court_decision_date && (
              <div>
                <p className="font-semibold text-white">Decision Date</p>
                <p className="text-white text-sm">
                  {new Date(report.court_decision_date).toLocaleDateString(
                    undefined,
                    {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    },
                  )}
                </p>
              </div>
            )}
          </>
        ) : (
          // Regular Daily Report Content
          <>
            <div>
              <p className="font-semibold text-gray-900 text-[15px]">
                Report Summary
              </p>
              <p className="text-gray-700 text-sm">
                <ReadMoreText
                  maxLines={3}
                  text={report.daily_report_summary || "-"}
                />
              </p>
              <p className="font-semibold text-gray-900 mt-2 text-[15px]">
                Next Appointment Reason
              </p>
              <p className="text-gray-700 text-sm">
                {report.next_appointment_reasons}
              </p>
              <p className="font-semibold text-gray-900 mt-2 text-[15px] mb-2">
                Court Order For Next Appointment
              </p>
              <p className="text-gray-700 text-sm">
                <ReadMoreText
                  maxLines={3}
                  text={report.court_order_for_next_appointment || "-"}
                />
              </p>
            </div>
          </>
        )}
      </div>
      {report.daily_report_summary_documents &&
        report.daily_report_summary_documents?.map((file, index) => (
          <FileRow file={file} {...fileRowProps} key={index} />
        ))}
      {report.court_decision_documents &&
        report.court_decision_documents?.map((file, index) => (
          <div className="bg-white rounded-3xl">
            <FileRow file={file} {...fileRowProps} key={index} />
          </div>
        ))}
      {report.daily_report_documents &&
        report.daily_report_documents?.map((file, index) => (
          <div className="bg-white rounded-3xl">
            <FileRow file={file} {...fileRowProps} key={index} />
          </div>
        ))}
      {hasAssets && (
        <div className="mt-4">
          <p
            className={`font-semibold mb-2 ${
              isFinalVerdict ? "text-white" : "text-gray-900 text-[15px]"
            }`}
          >
            Associated Asset
          </p>

          <ul className="list-disc list-inside">
            {assetsToDisplay.map((asset: any) => (
              <AssetDetailCard asset={asset} />
            ))}
          </ul>
        </div>
      )}
      {prohibitionId && (
        <div className="mt-4">
          <p
            className={`font-semibold mb-2 ${isFinalVerdict ? "text-white" : "text-gray-900 text-[15px]"}`}
          >
            Prohibition ID
          </p>
          <p
            className={`text-gray-700 text-sm ${isFinalVerdict ? "text-white" : ""}`}
          >
            {prohibitionId}
          </p>
        </div>
      )}

      {isFinalVerdict && hasAssets && !report?.is_appealed && (
        <button
          className="px-3 py-2 bg-white text-black rounded-lg hover:bg-gray-500 transition-colors text-sm my-2 hover:text-white"
          onClick={() => {
            setIsAppealPopupOpen?.({
              open: true,
              data: {
                report: report,
                assets: assetsToDisplay,
                currentAssetId: assetsToDisplay[0]?.id,
                parent_appeal: appealId,
                appeal_type: isAssetRecoveryReport
                  ? "asset_recovery"
                  : isAppeal
                    ? appeal?.appeal_type === "asset_recovery"
                      ? "re_appeal_on_asset_recovery"
                      : "re_appeal_on_prohibition"
                    : "prohibition",
              },
            });
          }}
        >
          Request Appeal
        </button>
      )}
      {report?.is_appealed && (
        <p className="font-semibold text-white text-sm mt-2">
          Appealed Requested
        </p>
      )}
    </div>
  );
}

export default DailyReportCard;
